import * as React from 'react'
import { Header, Row, Table as TanstackTable } from '@tanstack/react-table'

import { TableHeader } from './TableHeader'
import { TableBody } from './TableBody'
import { TablePaginationProps } from './TablePagination'
import { TableFooter } from './TableFooter'
import { SortingOrder } from '../SortingIconButton/SortingIconButton'
import { RefObject } from 'react'
import { SelectedTableHeader } from './SelectedTableHeader'
import classnames from 'classnames'
import { TableSummaryRow } from './TableSummaryRow/TableSummaryRow'

type TableStyle = 'table' | 'list'

type TableProps<T = unknown> = {
    table: TanstackTable<T>
    style?: TableStyle
    onTableColumnHeaderClick?: (header: Header<T, unknown>) => void
    getColumnSorting?: (header: Header<T, unknown>) => SortingOrder
    isRowClickable?: (row: Row<T>) => boolean
    onRowClick?: (row: Row<T>) => void
    containerRef?: RefObject<HTMLDivElement>
    tableWrapperRef?: RefObject<HTMLDivElement>
    tableRef?: RefObject<HTMLTableElement>
    tableBodyRef?: RefObject<HTMLTableSectionElement>
    selectedInvoiceIds?: string[]
    cellClassName?: string
    pagination?: TablePaginationProps
    summary?: { cells: React.ReactNode[] }
}

export const Table: React.FC<TableProps> = (props) => {
    const {
        table,
        onTableColumnHeaderClick,
        getColumnSorting,
        pagination,
        summary,
        style,
        isRowClickable,
        onRowClick,
        containerRef,
        tableWrapperRef,
        tableRef,
        tableBodyRef,
        selectedInvoiceIds,
    } = props

    const tableClass = classnames(
        'table-fixed border-separate border-spacing-0',
        'min-w-full'
    )

    const hasFooter = !!pagination

    const isSelectedHeaderVisible = selectedInvoiceIds
        ? selectedInvoiceIds.length > 0
        : null

    return (
        <div className="relative z-10 flex h-full flex-col" ref={containerRef}>
            <div className="shrink overflow-hidden rounded-xl border border-neutral-200">
                <div className="flex h-full flex-col">
                    <div
                        className="-mr-[1px] overflow-auto"
                        ref={tableWrapperRef}
                    >
                        <table className={tableClass} ref={tableRef}>
                            {isSelectedHeaderVisible ? (
                                <SelectedTableHeader table={table} />
                            ) : (
                                <TableHeader
                                    table={table}
                                    onTableColumnHeaderClick={
                                        onTableColumnHeaderClick
                                    }
                                    getColumnSorting={getColumnSorting}
                                    style={style}
                                />
                            )}
                            <TableBody
                                table={table}
                                style={style}
                                isRowClickable={isRowClickable}
                                onRowClick={onRowClick}
                                hasFooter={hasFooter}
                                tableBodyRef={tableBodyRef}
                                cellClassName={props.cellClassName}
                            />
                            {summary && (
                                <TableSummaryRow cells={summary.cells} />
                            )}
                        </table>
                    </div>
                    {hasFooter && <TableFooter pagination={pagination} />}
                </div>
            </div>
        </div>
    )
}
